import React, { MouseEventHandler, useState } from 'react';
import { Badge, Flex } from '@bynder/design-system';
import { MenuItem } from '../../types';
import { iconSizeMedium } from '../../helpers/iconSizes';

interface Props {
	items: MenuItem[];
	onItemClick: () => void;
}

export function Dropdown({ items, onItemClick }: Props) {
	const [pinnedItem, setPinnedItem] = useState<MenuItem | null>(null);

	const onDropdownItemClick = (item: MenuItem) => {
		if (item.menuItems?.length || item.Component) {
			setPinnedItem(item);
		}

		onItemClick();
	};

	return (
		<div className="admin-dropdown">
			<ul className="single">
				{items.map((i) => (
					<DropdownItem
						key={i.key}
						item={i}
						pinnedItem={pinnedItem}
						onClick={onDropdownItemClick}
						level={1}
					/>
				))}
			</ul>
		</div>
	);
}

interface ItemProps {
	item: MenuItem;
	pinnedItem: MenuItem | null;
	level: number;
	onClick: (item: MenuItem) => void;
}

/**
 * We are supporting some weird class names because of legacy css.
 * To understand the <a className>
 * - First level item 				className = "admin-option"
 * - First level item with children className = "admin-option sub"
 * - Second level item 				className = "admin-option-sub"
 *
 * To understand the <li className>
 * - No children 								className = ""
 * - Has children and is pinned					className = "sub noHover hover"
 * - Has children and another item is pinned	className = "sub noHover"
 * - Has children and nothing is pinned			className = "sub"
 */
function DropdownItem({ item, pinnedItem, onClick, level }: ItemProps) {
	const [isHover, setIsHover] = useState(false);
	const Icon = item.icon;

	if (!item.isVisible) {
		return null;
	}

	const onMouseDown: MouseEventHandler<HTMLLIElement> = () => {
		onClick(item);
	};

	const isPinned = item.label === pinnedItem?.label;
	const isOtherPinned = !!(pinnedItem && item.label !== pinnedItem?.label);
	const isOpen = isPinned || isHover;
	const hasChildren = !!item.menuItems?.length || !!item.Component;
	const liClassName = getLiClassName(hasChildren, isPinned, isOtherPinned);
	const aClassName = getAnchorClassName(hasChildren, level);

	const openDropdown = () => {
		if (pinnedItem && item.label !== pinnedItem?.label) {
			return;
		}

		setIsHover(true);
	};

	const closeDropdown = () => {
		setIsHover(false);
	};

	if (item.Component) {
		const Component = item.Component;

		return (
			<li
				className={`${item.componentClassName} ${
					item.componentIsButton ? '' : liClassName
				}`}
				onMouseDown={onMouseDown}
				onMouseEnter={openDropdown}
				onMouseLeave={closeDropdown}
			>
				<Component isOpen={isOpen} />
			</li>
		);
	}

	return (
		<li
			className={liClassName}
			onMouseDown={onMouseDown}
			onMouseEnter={openDropdown}
			onMouseLeave={closeDropdown}
		>
			{item.badgeLabel && item.href ? (
				<a href={item.href} className={aClassName}>
					<Flex justifyContent="space-between" alignItems="center">
						<span>
							{Icon && <Icon size={iconSizeMedium} />} {item.label}
						</span>
						<Badge isPillShaped>{item.badgeLabel}</Badge>
					</Flex>
				</a>
			) : (
				<a href={item.href} target={item.target || '_self'} className={aClassName}>
					{Icon && <Icon size={iconSizeMedium} />} {item.label}
				</a>
			)}

			{hasChildren && isOpen && (
				<div className="admin-dropdown-sub">
					<ul className="single-sub">
						{item.menuItems?.map((i) => (
							<DropdownItem
								key={i.key}
								item={i}
								pinnedItem={pinnedItem}
								onClick={onClick}
								level={level + 1}
							/>
						))}
					</ul>
				</div>
			)}
		</li>
	);
}

function getAnchorClassName(hasChildren: boolean, itemLevel: number) {
	const aClassName = 'admin-option';

	if (hasChildren) {
		return `${aClassName} sub`;
	}

	if (itemLevel > 1) {
		return `${aClassName}-sub`;
	}

	return aClassName;
}

function getLiClassName(hasChildren: boolean, isPinned: boolean, isOtherPinned: boolean) {
	if (!hasChildren) {
		return '';
	}

	if (isPinned) {
		return 'sub noHover hover';
	}

	if (isOtherPinned) {
		return 'sub noHover';
	}

	return 'sub';
}
